import jsPDF from 'jspdf';
import getLogoSrc from './logoUtils';

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB');
};

export const exportPurchaseOrderPdf = (org, order, orderLines = []) => {
  if (!org || !order) return;

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  const lineHeight = 18;
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = margin;

  const buildPDFContent = () => {
    doc.setFontSize(10);

    const halfPage = (pageWidth - margin * 2) / 2;
    const leftX = margin;
    const rightX = halfPage - 40;

    let leftY = y;
    doc.setFont(undefined, 'bold');
    doc.text(org.orgName || '', leftX, leftY);
    leftY += lineHeight;

    doc.setFont(undefined, 'normal');
    doc.text(org.orgAddress || '', leftX, leftY);
    leftY += lineHeight;

    doc.text(`${org.orgCity || ''} ${org.orgState || ''} ${org.orgZip || ''}`, leftX, leftY);
    leftY += lineHeight;

    doc.text(`${org.orgTaxIdDescription || ''}: ${org.orgTaxId || ''}`, leftX, leftY);

    let rightY = y;
    doc.setFont(undefined, 'bold');
    doc.text('Delivery Address', rightX, rightY);
    rightY += lineHeight;

    doc.setFont(undefined, 'normal');
    const deliveryAddressLines = String(order.deliveryAddress || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (deliveryAddressLines.length > 0) {
      deliveryAddressLines.forEach((line) => {
        doc.text(line, rightX, rightY);
        rightY += lineHeight;
      });
    } else {
      rightY += lineHeight;
    }

    doc.text(`${order.deliveryCity || ''} ${order.deliveryState || ''} ${order.deliveryZip || ''}`, rightX, rightY);
    rightY += lineHeight;

    doc.text(`Contact: ${order.deliveryContact || ''}`, rightX, rightY);
    rightY += lineHeight;

    doc.text(`Phone: ${order.deliveryPhone || ''}`, rightX, rightY);

    y = Math.max(leftY, rightY) + lineHeight;

    doc.setFontSize(16);
    doc.text('PURCHASE ORDER', pageWidth - margin - 150, margin + 20);

    y += 30;

    doc.setFontSize(10);
    const columnWidth = (pageWidth - margin * 2) / 3;
    const col1X = margin;
    const col2X = margin + columnWidth;
    const col3X = margin + columnWidth * 2;

    const drawHeaderRow = (label1, val1, label2, val2, label3, val3) => {
      doc.setFont(undefined, 'bold');
      if (label1) doc.text(`${label1}:`, col1X, y);
      if (label2) doc.text(`${label2}:`, col2X, y);
      if (label3) doc.text(`${label3}:`, col3X, y);

      doc.setFont(undefined, 'normal');
      if (val1) doc.text(`${val1}`, col1X + 50, y);
      if (val2) doc.text(`${val2}`, col2X + 70, y);
      if (val3) doc.text(`${val3}`, col3X + 90, y);

      y += lineHeight;
    };

    drawHeaderRow('Supplier', order.supplier?.name || '', '', '', 'PO Number', order.poNumber || '');
    drawHeaderRow('', '', '', '', 'PO Date', formatDate(order.orderDate));
    drawHeaderRow('', '', '', '', 'PO Reference', order.customField1 || 'N/A');
    drawHeaderRow('', '', '', '', 'Delivery Date', formatDate(order.deliveryDate));
    drawHeaderRow('', '', '', '', 'Created By', order.createdByUser?.name || 'Steve Bryce');
    drawHeaderRow('', '', '', '', 'Approval Date', formatDate(order.approvedDate));
    drawHeaderRow('', '', '', '', 'Approved By', order.approvedByUser?.name || '');

    y += 10;

    const headers = ['Item Code', 'Description', 'Qty', 'Unit Price', 'Tax %', 'Total'];
    const colWidths = [80, 220, 40, 70, 50, 60];
    const colX = [];

    let x = margin;
    headers.forEach((h, i) => {
      colX[i] = x;
      doc.setFont(undefined, 'bold');
      doc.text(h, x, y);
      x += colWidths[i];
    });

    const lineY1 = y + 6;
    doc.setLineWidth(0.2);
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, lineY1, pageWidth - margin, lineY1);

    y += lineHeight;
    doc.setFont(undefined, 'normal');

    orderLines.forEach((ln) => {
      const values = [
        ln.itemCode || '',
        ln.description || '',
        ln.quantity != null ? String(ln.quantity) : '',
        ln.unitPrice != null ? Number(ln.unitPrice).toFixed(2) : '',
        ln.taxRate != null ? String(ln.taxRate) : '',
        ln.lineTotal != null ? Number(ln.lineTotal).toFixed(2) : '',
      ];

      values.forEach((val, i) => {
        doc.text(String(val), colX[i], y, {
          maxWidth: colWidths[i] - 4,
        });
      });

      const lineY2 = y + 6;
      doc.setLineWidth(0.2);
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, lineY2, pageWidth - margin, lineY2);

      y += lineHeight;

      if (y > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        y = margin;
        colX.forEach((x, i) => {
          doc.setFont(undefined, 'bold');
          doc.text(headers[i], x, y);
        });
        y += lineHeight;
        doc.setFont(undefined, 'normal');
      }
    });

    const totalTax = orderLines.reduce((sum, l) => {
      const qty = Number(l.quantity || 0);
      const unitPrice = Number(l.unitPrice || 0);
      const taxRate = Number(l.taxRate || 0);
      return sum + qty * unitPrice * (taxRate / 100);
    }, 0).toFixed(2);

    const total = orderLines.reduce((sum, l) => sum + Number(l.lineTotal || 0), 0).toFixed(2);

    y += 10;
    doc.setFont(undefined, 'bold');
    doc.text('Tax Total:', colX[headers.length - 3], y);
    doc.text(`$${totalTax}`, colX[headers.length - 1], y);

    y += 20;
    doc.setFont(undefined, 'bold');
    doc.text('Total Amount:', colX[headers.length - 3], y);
    doc.text(`$${total}`, colX[headers.length - 1], y);

    doc.save(`purchase_order_${order.poNumber}.pdf`);
  };

  const logoSrc = getLogoSrc(org.orgLogo);
  const logoHeight = 80;
  if (logoSrc) {
    if (logoSrc.startsWith('data:')) {
      const match = logoSrc.match(/^data:image\/(\w+);/);
      const format = match ? match[1].toUpperCase() : 'PNG';
      try {
        const props = doc.getImageProperties(logoSrc);
        const logoWidth = (props.width / props.height) * logoHeight;
        doc.addImage(logoSrc, format, margin, y, logoWidth, logoHeight);
        y += logoHeight + 10;
      } catch {
        console.warn("Logo properties couldn't be determined. Proceeding without it.");
      }
      buildPDFContent();
    } else {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = logoSrc;
      img.onload = () => {
        const logoWidth = (img.width / img.height) * logoHeight;
        doc.addImage(img, 'PNG', margin, y, logoWidth, logoHeight);
        y += logoHeight + 10;
        buildPDFContent();
      };
      img.onerror = () => {
        console.warn("Logo couldn't load. Proceeding without it.");
        buildPDFContent();
      };
    }
  } else {
    buildPDFContent();
  }
};

export default exportPurchaseOrderPdf;

